import React from 'react';
import { Link } from 'react-router-dom';
import { Calendar, User, ArrowRight, Clock } from 'lucide-react';

const BlogsPage = () => {
  const blogPosts = [
    {
      id: 1,
      title: 'Understanding Trademark Classes: A Complete Guide',
      excerpt: 'Learn about the 45 international trademark classes and how to choose the right one for your business. This comprehensive guide breaks down each class with real-world examples.',
      author: 'Sarah Johnson',
      date: 'March 15, 2024',
      readTime: '8 min read',
      category: 'Education',
      image: 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=800&h=600&fit=crop'
    },
    {
      id: 2,
      title: 'Common Trademark Registration Mistakes to Avoid',
      excerpt: 'Discover the top 10 mistakes businesses make when filing for trademark registration and how to avoid them. Save time and money by getting it right the first time.',
      author: 'Michael Chen',
      date: 'March 10, 2024',
      readTime: '6 min read',
      category: 'Tips & Tricks',
      image: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=800&h=600&fit=crop'
    },
    {
      id: 3,
      title: 'How Long Does Trademark Registration Take?',
      excerpt: 'A detailed timeline of the trademark registration process, from initial filing to approval. Understand each stage and what to expect along the way.',
      author: 'Emily Rodriguez',
      date: 'March 5, 2024',
      readTime: '5 min read',
      category: 'Process',
      image: 'https://images.unsplash.com/photo-1553729459-efe14ef6055d?w=800&h=600&fit=crop'
    },
    {
      id: 4,
      title: 'Trademark vs Copyright: What\'s the Difference?',
      excerpt: 'Confused about intellectual property protection? This article explains the key differences between trademarks, copyrights, and patents with clear examples.',
      author: 'David Thompson',
      date: 'February 28, 2024',
      readTime: '7 min read',
      category: 'Education',
      image: 'https://images.unsplash.com/photo-1589829545856-d10d557cf95f?w=800&h=600&fit=crop'
    },
    {
      id: 5,
      title: 'International Trademark Protection: Going Global',
      excerpt: 'Planning to expand internationally? Learn about the Madrid Protocol and how to protect your trademark in multiple countries efficiently.',
      author: 'Lisa Anderson',
      date: 'February 20, 2024',
      readTime: '10 min read',
      category: 'International',
      image: 'https://images.unsplash.com/photo-1526304640581-d334cdbbf45e?w=800&h=600&fit=crop'
    },
    {
      id: 6,
      title: 'The Cost of Trademark Registration: A Breakdown',
      excerpt: 'Understand all the costs involved in trademark registration, from filing fees to attorney costs. Plan your budget with our comprehensive breakdown.',
      author: 'Robert Kim',
      date: 'February 15, 2024',
      readTime: '6 min read',
      category: 'Finance',
      image: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=800&h=600&fit=crop'
    }
  ];

  const categories = ['All', 'Education', 'Tips & Tricks', 'Process', 'International', 'Finance'];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-[#168356] via-[#0a5938] to-[#1a9b6b] text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Trademark Insights & Resources
            </h1>
            <p className="text-xl text-white/90 max-w-3xl mx-auto">
              Stay informed with expert articles, guides, and insights on trademark registration, protection, and intellectual property law
            </p>
          </div>
        </div>
      </div>

      {/* Category Filter */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-wrap gap-3 justify-center">
            {categories.map((category, index) => (
              <button
                key={index}
                className={`px-6 py-2 rounded-full font-medium transition-all duration-300 ${
                  index === 0
                    ? 'bg-[#168356] text-white shadow-lg shadow-[#168356]/30'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Blog Posts Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {blogPosts.map((post) => (
            <article
              key={post.id}
              className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-2xl transition-all duration-300 group"
            >
              {/* Image */}
              <div className="relative h-48 overflow-hidden">
                <img
                  src={post.image}
                  alt={post.title}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute top-4 left-4">
                  <span className="bg-[#168356] text-white px-3 py-1 rounded-full text-xs font-semibold">
                    {post.category}
                  </span>
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                {/* Meta Info */}
                <div className="flex items-center gap-4 text-sm text-gray-500 mb-3">
                  <div className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    <span>{post.date}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    <span>{post.readTime}</span>
                  </div>
                </div>

                {/* Title */}
                <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-[#168356] transition-colors">
                  {post.title}
                </h3>

                {/* Excerpt */}
                <p className="text-gray-600 mb-4 line-clamp-3">
                  {post.excerpt}
                </p>

                {/* Author & CTA */}
                <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4 text-gray-400" />
                    <span className="text-sm text-gray-600">{post.author}</span>
                  </div>
                  <Link
                    to="#"
                    className="flex items-center gap-1 text-[#168356] font-semibold text-sm hover:gap-2 transition-all"
                  >
                    Read More
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            </article>
          ))}
        </div>

        {/* Load More Button */}
        <div className="text-center mt-12">
          <button className="bg-[#d29b00] hover:bg-[#a87b00] text-white font-bold px-8 py-4 rounded-lg transition-all duration-300 transform hover:scale-105 shadow-lg shadow-[#d29b00]/30">
            Load More Articles
          </button>
        </div>
      </div>

      {/* Newsletter Section */}
      <div className="bg-gradient-to-br from-gray-900 via-black to-gray-900 text-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Stay Updated with Our Newsletter
          </h2>
          <p className="text-gray-300 mb-8">
            Get the latest trademark insights, tips, and updates delivered to your inbox
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center max-w-md mx-auto">
            <input
              type="email"
              placeholder="Enter your email"
              className="flex-1 px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#168356]"
            />
            <button className="bg-[#d29b00] hover:bg-[#a87b00] text-white font-bold px-6 py-3 rounded-lg transition-all duration-300">
              Subscribe
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlogsPage;
